import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2Icon,
  CalendarClockIcon,
  CreditCardIcon,
  DatabaseIcon,
  FileSignatureIcon,
  KanbanSquareIcon,
  LayoutDashboardIcon,
  SettingsIcon,
  UserPlusIcon,
} from 'lucide-react';
import type { NavGroup } from '@opengoal/ui';
import { useOffboardingsQuery } from '@/modules/offboarding';
import { useOnboardingsQuery } from '@/modules/onboarding';
import { useMyOpenTaskCountQuery } from '@/modules/tasks';

/**
 * Sidebar navigation for the platform app.
 *
 * Keys are route paths — `AppShell` reports the clicked key and the layout
 * navigates to it, so adding a nav entry means adding the matching route in the
 * module's `routes.tsx`.
 *
 * When the backend's RBAC milestone lands, gate entries with `visible:` driven
 * by the CASL ability rather than rendering links the user will only get a 403
 * from.
 */
export function useNavGroups(): NavGroup[] {
  const { t } = useTranslation();

  // Red counts beside Onboarding and Offboarding: how many are still open.
  // Both read the list their own screens load, so opening either page is free.
  const onboardings = useOnboardingsQuery();
  const offboardings = useOffboardingsQuery();
  const openOnboardings = (onboardings.data?.data ?? []).filter(
    (row) => row.status === 'in_progress',
  ).length;
  const openOffboardings = (offboardings.data?.data ?? []).filter(
    (row) => row.status === 'in_progress',
  ).length;
  // Open tasks on my plate, directly or through a team.
  const myOpenTasks = useMyOpenTaskCountQuery().data ?? 0;

  return useMemo<NavGroup[]>(
    () => [
      {
        title: t('nav.overview'),
        items: [
          { key: '/', label: t('nav.dashboard'), icon: <LayoutDashboardIcon /> },
          {
            key: '/tasks',
            label: t('nav.tasks'),
            icon: <KanbanSquareIcon />,
            badge: myOpenTasks,
          },
          { key: '/players', label: t('nav.players'), icon: <DatabaseIcon /> },
          {
            key: '/onboarding',
            label: t('nav.onboarding'),
            icon: <UserPlusIcon />,
            badge: openOnboardings,
          },
          // {
          //   key: '/offboarding',
          //   label: t('nav.offboarding'),
          //   icon: <UserMinusIcon />,
          //   badge: openOffboardings,
          // },
          { key: '/contracts', label: t('nav.contracts'), icon: <FileSignatureIcon /> },
        ],
      },
      {
        title: t('nav.dealmaking'),
        items: [
          // Hidden until the screens have content.
          // { key: '/requests', label: t('nav.requests'), icon: <InboxIcon /> },
          // { key: '/deals', label: t('nav.deals'), icon: <ArrowLeftRightIcon /> },
          {
            key: '/transfer-windows',
            label: t('nav.transferWindows'),
            icon: <CalendarClockIcon />,
          },
          { key: '/clubs', label: t('nav.clubs'), icon: <Building2Icon /> },
        ],
      },
      {
        title: t('nav.system'),
        items: [
          { key: '/settings', label: t('nav.settings'), icon: <SettingsIcon /> },
          { key: '/billing', label: t('nav.billing'), icon: <CreditCardIcon /> },
        ],
      },
    ],
    [t, openOnboardings, openOffboardings, myOpenTasks],
  );
}
